<template>
	<panel class="completion-panel-section" :show="show" @close="close">
		<template #title>
			Add Note
		</template>
		<template>
			<form
				class="panel-form panel-form-textarea-fullheight"
				@submit.prevent="onFormSubmit"
			>
				<img
					src="@/assets/img/undraw-noted.svg"
					class="panel-illustration"
				/>
				<span class="mb-4 font-roboto text-center">
					Enter Completion Note
					{{ completionNoteRequired ? '(Required)' : '' }}
				</span>
				<CompletionNoteTextArea
					ref="completionTextarea"
					v-model="message"
					:is-required="completionNoteRequired"
					:template="completionTemplate"
					:max-char-length="maxCharLength"
					:min-char-length="minCharLength"
					:textarea-placeholder="'Enter new note here...'"
					:ref-attr="'textarea'"
					@char-count-change="charCount = $event"
				/>
				<div class="flex justify-between mt-3">
					<button
						type="button"
						class="btn btn-danger w-50 mr-2"
						@click.prevent="close"
					>
						<div class="btn-ripple" />
						<font-awesome-icon
							class="btn-icon"
							:icon="['far', 'times-octagon']"
						/>
						<span class="btn-label">Cancel</span>
					</button>
					<button
						ref="last"
						type="submit"
						:class="[
							'btn',
							'w-50',
							'ml-2',
							isValidMessage ? 'btn-outline-success' : '',
						]"
						:aria-disabled="!isValidMessage"
						:disabled="isCallCompleting"
						@keydown.tab="onLastElementTab"
					>
						<div class="btn-ripple" />
						<font-awesome-icon
							v-if="isCallCompleting"
							class="btn-icon fa-spin mr-2"
							:icon="['far', 'circle-notch']"
						/>
						<font-awesome-icon
							v-else
							class="btn-icon"
							:icon="['far', 'check']"
							aria-hidden="true"
						/>
						<span class="btn-label">
							Complete
						</span>
					</button>
				</div>
			</form>
		</template>
	</panel>
</template>
<script>
import Panel from './Panel'
import PanelMixin from '@/mixins/PanelMixin'
import util from '@/util'
import { mapActions, mapGetters } from 'vuex'
import CompletionNoteTextArea from '../CompletionNoteTextArea.vue'
import {
	NOTES_MAX_LENGTH,
	COMPLETION_NOTE_MIN_LENGTH,
} from '@/config/constants'

export default {
	/**
	 * The component's registered child components.
	 *
	 * @type {Object}
	 */
	components: {
		Panel,
		CompletionNoteTextArea,
	},

	/**
	 * The component's computed properties.
	 *
	 * @type {Object}
	 */
	computed: {
		/**
		 * Get the current active call.
		 *
		 * @return {Object}
		 */
		call() {
			return this.findBySid(this.sid)
		},

		completionTemplate() {
			return (this.activePartner?.complete_note_template || '')
				.replace(/\r\n|\r/g, '\n')
				.trim()
				.replace(/([:-])[ \t]+(?=\n)/g, '$1')
		},

		completionNoteRequired() {
			return Boolean(this.activePartner?.completion_note_required)
		},

		isValidMessage() {
			// Use the charCount from data property (emitted by child component)
			if (this.completionNoteRequired) {
				return (
					this.charCount >= this.minCharLength &&
					this.charCount <= this.maxCharLength
				)
			} else {
				return this.charCount <= this.maxCharLength
			}
		},

		/**
		 * Determine if a call is urgent.
		 *
		 * @return {Boolean}
		 */
		isUrgent() {
			if (this.call) {
				return this.call.is_urgent
			}

			return false
		},

		...mapGetters({
			findBySid: 'calls/findBySid',
			activePartner: 'partners/active',
		}),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		/**
		 * Handle the form submit event.
		 *
		 * @return {void}
		 */
		async onFormSubmit() {
			const component = this.$refs.completionTextarea
			if (component && !component.validateText()) {
				return
			}
			try {
				this.isCallCompleting = true
				await this.complete({
					sid: this.sid,
					message:
						util.normalizeString(this.completionTemplate) ===
						util.normalizeString(this.message)
							? ''
							: this.message.trim(),
					closeDrawer: this.close,
				})
				this.message = ''
				this.$alert.success('calls.complete.success')
			} catch (e) {
				this.$alert.error('calls.complete.error')
			} finally {
				this.isCallCompleting = false
				this.close()
			}
		},

		handleSetMessage() {
			const storedMessage = util.getDraftValue('complete_call', this.sid)
			if (!storedMessage && this.completionTemplate) {
				return (this.message = this.completionTemplate)
			}
			return (this.message = storedMessage || '')
		},

		focusCompletionTextarea() {
			this.$nextTick(() => {
				setTimeout(() => {
					const component = this.$refs.completionTextarea
					if (
						component &&
						component.$refs &&
						component.$refs.textarea
					) {
						const textarea = component.$refs.textarea
						if (textarea) {
							textarea.focus()
							textarea.setSelectionRange(0, 0)
						}
					}
				}, 200)
			})
		},

		...mapActions({
			complete: 'calls/complete',
			get: 'calls/get',
		}),
	},

	/**
	 * The component's mergeable mixins.
	 *
	 * @type {Array}
	 */
	mixins: [PanelMixin],

	/**
	 * The component's name used for debugging.
	 *
	 * @type {String}
	 */
	name: 'CompleteCallPanel',

	/**
	 * The component's inherited properties.
	 *
	 * @type {Object}
	 */
	props: {
		/**
		 * The call's sid to be dismissed.
		 *
		 * @type {String}
		 */
		sid: {
			required: true,
			validator: prop => typeof prop === 'string' || prop === null,
		},
	},

	watch: {
		show(isVisible) {
			if (isVisible) {
				if (this.completionNoteRequired) {
					this.focusCompletionTextarea()
				}
				this.handleSetMessage()
			}
		},
		message() {
			util.setDraftValue('complete_call', this.message, this.sid)
		},
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			message: '',
			isCallCompleting: false,
			charCount: 0,
			maxCharLength: NOTES_MAX_LENGTH,
			minCharLength: COMPLETION_NOTE_MIN_LENGTH,
		}
	},
}
</script>
